import { type RefObject, useEffect, useState } from 'react'
import { playbackProgress } from './lib/playback.js'
import { computeScrollProgress } from './lib/scrub.js'
import styles from './ScrollProgress.module.css'

export interface ScrollProgressProps {
  active: boolean
  heroVideoRef: RefObject<HTMLVideoElement | null>
  heroScrollMode?: boolean
  startRef: RefObject<HTMLElement | null>
  endRef: RefObject<HTMLElement | null>
  endingEndRef: RefObject<HTMLElement | null>
}

type ProgressSection = 'hero' | 'content' | 'ending'

interface ProgressState {
  progress: number
  section: ProgressSection
}

const SECTION_LABELS: Record<ProgressSection, string> = {
  hero: '상단 영상 재생 진행률',
  content: '본문 스크롤 진행률',
  ending: '하단 영상 스크롤 진행률',
}

/** 상단 영상·본문·하단 영상이 각자 0→100%로 진행되는 상단 표시줄이다. */
export function ScrollProgress({
  active, heroVideoRef, heroScrollMode = false, startRef, endRef, endingEndRef,
}: ScrollProgressProps) {
  const [state, setState] = useState<ProgressState | null>(null)

  useEffect(() => {
    let scrollFrame: number | undefined
    const update = () => {
      if (!active) {
        setState(null)
        return
      }
      const startElement = startRef.current
      const endElement = endRef.current
      const endingEndElement = endingEndRef.current
      if (startElement === null || endElement === null || endingEndElement === null) return

      const scrollY = window.scrollY
      const start = startElement.getBoundingClientRect().top + scrollY
      const end = endElement.getBoundingClientRect().top + scrollY
      const endingEnd = endingEndElement.getBoundingClientRect().top + scrollY
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight
      // 첫 자동 재생/재방문 스크럽 모두 본문 상단이 화면 상단에 닿을 때 전환한다.
      const contentStart = start
      // 엔딩 영상 스크럽은 섹션 상단이 뷰포트 상단에 닿을 때 0%에서 시작한다.
      const endingStart = end
      const endingFinish = endingEnd - viewportHeight

      if (
        scrollY < 0
        || contentStart <= 0
        || endingStart <= contentStart
        || endingFinish <= endingStart
      ) {
        setState(null)
        return
      }

      if (scrollY < contentStart) {
        const v = heroVideoRef.current
        setState({
          progress: heroScrollMode
            ? computeScrollProgress(-scrollY, contentStart, v?.parentElement?.clientHeight || window.innerHeight)
            : v === null ? 0 : playbackProgress(v.currentTime, v.duration),
          section: 'hero',
        })
        return
      }
      if (scrollY < endingStart) {
        setState({
          progress: (scrollY - contentStart) / (endingStart - contentStart),
          section: 'content',
        })
        return
      }
      if (scrollY <= endingFinish) {
        setState({
          progress: (scrollY - endingStart) / (endingFinish - endingStart),
          section: 'ending',
        })
        return
      }
      setState(null)
    }
    const updateAfterScroll = () => {
      if (scrollFrame !== undefined) return
      scrollFrame = window.requestAnimationFrame(() => {
        scrollFrame = undefined
        update()
      })
    }

    update()
    window.addEventListener('scroll', updateAfterScroll, { passive: true })
    window.addEventListener('resize', update)
    window.visualViewport?.addEventListener('resize', update)
    const layoutElement = startRef.current?.parentElement
    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(update)
    if (layoutElement !== null && layoutElement !== undefined) {
      resizeObserver?.observe(layoutElement)
    }
    return () => {
      window.removeEventListener('scroll', updateAfterScroll)
      window.removeEventListener('resize', update)
      window.visualViewport?.removeEventListener('resize', update)
      resizeObserver?.disconnect()
      if (scrollFrame !== undefined) window.cancelAnimationFrame(scrollFrame)
    }
  }, [active, endRef, endingEndRef, startRef, heroVideoRef, heroScrollMode])

  const inHero = active && state?.section === 'hero'
  useEffect(() => {
    const v = heroVideoRef.current
    if (!inHero || heroScrollMode || v === null) return
    let frame: number | undefined
    const update = () => {
      const progress = playbackProgress(v.currentTime, v.duration)
      setState((previous) => previous?.section === 'hero' && previous.progress !== progress
        ? { section: 'hero', progress }
        : previous)
    }
    const stop = () => {
      if (frame !== undefined) window.cancelAnimationFrame(frame)
      frame = undefined
      update()
    }
    const tick = () => {
      update()
      frame = !v.paused && !v.ended ? window.requestAnimationFrame(tick) : undefined
    }
    const start = () => {
      stop()
      tick()
    }
    update()
    if (!v.paused && !v.ended) start()
    v.addEventListener('playing', start)
    v.addEventListener('timeupdate', update)
    v.addEventListener('loadedmetadata', update)
    v.addEventListener('pause', stop)
    v.addEventListener('waiting', stop)
    v.addEventListener('ended', stop)
    return () => {
      if (frame !== undefined) window.cancelAnimationFrame(frame)
      v.removeEventListener('playing', start)
      v.removeEventListener('timeupdate', update)
      v.removeEventListener('loadedmetadata', update)
      v.removeEventListener('pause', stop)
      v.removeEventListener('waiting', stop)
      v.removeEventListener('ended', stop)
    }
  }, [inHero, heroVideoRef, heroScrollMode])

  if (state === null) return null

  const percent = Math.round(state.progress * 100)
  const palette = state.section === 'content' ? 'content' : 'film'
  return (
    <div
      className={styles.track}
      role="progressbar"
      aria-label={SECTION_LABELS[state.section]}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      data-palette={palette}
    >
      <div className={styles.fill} style={{ transform: `scaleX(${state.progress})` }} />
    </div>
  )
}
